import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import UserMenu from "../components/UserMenu";
import StartBuildingButton from "../components/StartBuildingButton";
import { useAuth } from "../contexts/AuthContext";
import useDocumentMeta from "../lib/useDocumentMeta";
// Content extracted to /app/frontend/src/shared/ontario_report_card_comments.json
// (pattern mirrors the_path_content.json). The page reads from it — no comment
// text is hardcoded in the JSX. Adding comments later means editing only the JSON.
import commentBank from "../shared/ontario_report_card_comments.json";

const SectionKicker = ({ children, testid }) => (
  <p
    data-testid={testid}
    className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70"
  >
    {children}
  </p>
);

const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Copy-to-clipboard button with a brief on-button confirmation state.
const CopyButton = ({ text, testid }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers / insecure contexts.
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onCopy}
      aria-label={copied ? "Copied" : "Copy comment"}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold border transition-colors ${
        copied
          ? "bg-[#96ded1]/25 border-[#1c9e7e]/40 text-[#177a61]"
          : "bg-white border-[#4a6984]/25 text-[#4a6984] hover:bg-[#f4f5f7]"
      }`}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const GradeSection = ({ gradeBlock, open, onToggle }) => {
  const gradeLabel = `Grade ${gradeBlock.grade}`;
  const gradeSlug = slug(gradeLabel);

  return (
    <section
      id={`grade-${gradeBlock.grade}`}
      data-testid={`rc-grade-section-${gradeSlug}`}
      className="ae-card overflow-hidden shadow-sm scroll-mt-24"
    >
      <button
        type="button"
        data-testid={`rc-grade-toggle-${gradeSlug}`}
        aria-expanded={open}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 md:px-6 md:py-5 text-left hover:bg-[#f4f5f7]/60 transition-colors"
      >
        <span className="font-extrabold text-xl md:text-2xl text-[#1c2d3d] tracking-tight">
          {gradeLabel}
        </span>
        <ChevronDown
          className={`h-5 w-5 text-[#4a6984] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div
          data-testid={`rc-grade-panel-${gradeSlug}`}
          className="px-5 pb-6 md:px-6 md:pb-7 border-t border-[#4a6984]/10"
        >
          {gradeBlock.subjects.map((subj) => {
            const subjSlug = slug(subj.subject);
            return (
              <div
                key={subj.subject}
                data-testid={`rc-subject-${gradeSlug}-${subjSlug}`}
                className="mt-6"
              >
                <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight">
                  {subj.subject}
                  {subj.strand ? (
                    <span className="ml-2 text-sm font-semibold text-[#4a6984]/70">
                      · {subj.strand}
                    </span>
                  ) : null}
                </h3>

                {subj.levels.map((lvl) => {
                  const levelSlug = slug(lvl.level);
                  return (
                    <div key={lvl.level} className="mt-3">
                      <p
                        data-testid={`rc-level-${gradeSlug}-${subjSlug}-${levelSlug}`}
                        className="font-bold uppercase text-[11px] tracking-[0.16em] text-[#4a6984]/70"
                      >
                        {lvl.level}
                      </p>
                      <div className="mt-2 space-y-2.5">
                        {lvl.comments.map((text, idx) => (
                          <article
                            key={idx}
                            data-testid={`rc-comment-card-${gradeSlug}-${subjSlug}-${levelSlug}-${idx}`}
                            className="ae-card border-l-[3px] border-l-[#96ded1] p-4 flex items-start gap-3"
                          >
                            <p className="flex-1 text-[15px] text-[#4a6984] font-medium leading-relaxed">
                              {text}
                            </p>
                            <CopyButton
                              text={text}
                              testid={`rc-copy-${gradeSlug}-${subjSlug}-${levelSlug}-${idx}`}
                            />
                          </article>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

const OntarioReportCards = () => {
  const { user } = useAuth();

  useDocumentMeta({
    title:
      "Ontario Report Cards — Progress Reports, Term 1 and Term 2 | Atomic Edu",
    description:
      "How Ontario elementary report cards work — the three reports, the six learning skills, and what belongs in a comment. Written for Grades 4–8 teachers.",
  });

  // Deep links — each grade has a URL hash (#grade-7). The first grade is
  // open by default; a matching hash on load (or a later hashchange) opens
  // that grade and scrolls to it, so teachers can bookmark and share a link
  // straight to their grade.
  const gradeNums = commentBank.grades.map((g) => g.grade);
  const [openGrades, setOpenGrades] = useState(() => ({ [gradeNums[0]]: true }));

  const openFromHash = useCallback(() => {
    const m = (window.location.hash || "").match(/^#grade-(\d+)$/);
    if (!m) return;
    const n = Number(m[1]);
    if (!gradeNums.includes(n)) return;
    setOpenGrades((prev) => ({ ...prev, [n]: true }));
    // Expand first, then scroll to the freshly opened section.
    setTimeout(() => {
      document.getElementById(`grade-${n}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [openFromHash]);

  const toggleGrade = (n) => {
    const willOpen = !openGrades[n];
    setOpenGrades((prev) => ({ ...prev, [n]: willOpen }));
    // Reflect the open grade in the URL so the link is shareable, without
    // pushing a new history entry or triggering a scroll jump.
    if (willOpen) {
      window.history.replaceState(null, "", `#grade-${n}`);
    } else if (window.location.hash === `#grade-${n}`) {
      window.history.replaceState(null, "", "#comments");
    }
  };

  return (
    <div
      className="min-h-screen bg-[#f4f5f7] text-[#4a6984]"
      data-testid="ontario-report-cards-page"
    >
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#1c2d3d]/95 border-b border-[#1c2d3d] shadow-lg shadow-[#1c2d3d]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link
            to="/"
            data-testid="brand-logo"
            className="font-black text-base sm:text-lg tracking-tight text-white/80 hover:text-white inline-flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            <img
              src="/brand/atomicedu-logo-mark.png"
              alt="Atomic Edu"
              className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0"
            />
            Atomic Edu
          </Link>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <span className="hidden md:inline-flex">
              <StartBuildingButton />
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main
        data-testid="rc-main"
        className="pt-32 pb-24 max-w-[760px] mx-auto px-6"
      >
        {/* SECTION 1 — Page header */}
        <section data-testid="rc-header">
          <SectionKicker testid="rc-header-kicker">
            Ontario progress reports
          </SectionKicker>
          <h1
            data-testid="rc-headline"
            className="mt-3 font-black text-4xl sm:text-5xl text-[#1c2d3d] tracking-tight leading-[1.05]"
          >
            Progress report comments for Grades 4 to 8.
          </h1>
          <p
            data-testid="rc-lede"
            className="mt-5 text-[#4a6984]/85 font-medium text-base sm:text-lg leading-relaxed"
          >
            One hundred free comments, organised by grade and subject, ready to
            copy and adapt. Math covers all three levels. Every other subject
            covers progressing with difficulty, because that is the only case
            the progress report asks you to comment on.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              asChild
              data-testid="rc-header-cta-browse"
              className="ae-cta h-12 px-6 rounded-lg text-sm font-bold inline-flex items-center gap-2"
            >
              <a href="#comments">
                Browse the comments
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="rc-header-cta-builder"
              className="h-12 px-6 rounded-lg text-sm font-bold border-[#4a6984]/25 text-[#1c2d3d] hover:bg-[#1c2d3d]/5 inline-flex items-center gap-2"
            >
              <Link to="/pricing">See how the builder works</Link>
            </Button>
          </div>
        </section>

        {/* SECTION 2 — How to use */}
        <section data-testid="rc-how-to-use" className="mt-14">
          <p className="text-[#4a6984] font-medium text-base md:text-lg leading-relaxed">
            Every comment uses [Name] and [He/She] as placeholders. Copy the one
            you want, paste it into your reporting program, and use find and
            replace to drop in the student and the pronouns.
          </p>
          <p className="mt-4 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed">
            These are progress report comments, written in the present tense for
            the early weeks of the year. They are a starting point, not a
            finished comment. Adjust them to the student in front of you.
          </p>
        </section>

        {/* SECTION 3 — Comments */}
        <section id="comments" data-testid="rc-comments" className="mt-16 scroll-mt-24">
          <div className="space-y-4">
            {commentBank.grades.map((gradeBlock) => (
              <GradeSection
                key={gradeBlock.grade}
                gradeBlock={gradeBlock}
                open={Boolean(openGrades[gradeBlock.grade])}
                onToggle={() => toggleGrade(gradeBlock.grade)}
              />
            ))}
          </div>
        </section>

        {/* SECTION 4 — Why some subjects only have one level */}
        <section data-testid="rc-why-only-difficulty" className="mt-16">
          <SectionKicker testid="rc-why-only-difficulty-kicker">
            Why some subjects only have one level
          </SectionKicker>
          <p
            data-testid="rc-why-only-difficulty-copy"
            className="mt-3 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
          >
            On the progress report, subjects other than Math and Language
            receive a comment only when a student is progressing with
            difficulty. That is why Math here has all three levels and Science,
            the Arts, Health, Physical Education and French have one. The bank
            matches the report.
          </p>
        </section>

        {/* SECTION 5 — Where the time actually goes (surviving section) */}
        <section data-testid="rc-the-real-problem" className="mt-16">
          <SectionKicker testid="rc-the-real-problem-kicker">
            Where the time actually goes
          </SectionKicker>
          <div
            className="mt-6 ae-card rounded-lg border-l-[3px] px-6 py-7 md:px-8 md:py-8 shadow-sm"
            style={{ borderLeftColor: "#96ded1" }}
          >
            <p
              data-testid="rc-the-real-problem-p1"
              className="text-lg md:text-xl font-extrabold text-[#1c2d3d] leading-snug"
            >
              You do not sit down in November short of words. You sit down short
              of records.
            </p>
            <p
              data-testid="rc-the-real-problem-p2"
              className="mt-5 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
            >
              Which expectations did you actually cover in Term 1? Which unit got
              cut when the assembly ran long? Which students did you observe doing
              the thing you are about to claim they can do?
            </p>
            <p
              data-testid="rc-the-real-problem-p3"
              className="mt-5 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
            >
              Most teachers reconstruct this from memory, a planner, and a
              gradebook that was never designed to answer the question. That
              reconstruction is the work. The writing is the easy part once you
              have it.
            </p>
            <p
              data-testid="rc-the-real-problem-p4"
              className="mt-5 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
            >
              Atomic Edu was built the other way round. You plan the unit, you
              mark it taught, and when the reporting period comes the comment is
              written from the lessons you actually delivered, with the curriculum
              expectations already attached.
            </p>
            <p
              data-testid="rc-the-real-problem-p5"
              className="mt-5 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
            >
              You still write the comment. You still make every judgment. Nothing
              is auto-calculated, and no student names are ever stored. What
              changes is that you are not starting from a blank box and a foggy
              memory.
            </p>
          </div>
        </section>

        {/* SECTION 6 — Closing (surviving section) */}
        <section data-testid="rc-closing" className="mt-20 text-center">
          <p
            data-testid="rc-closing-copy"
            className="text-[#1c2d3d] font-semibold text-lg md:text-xl leading-relaxed max-w-[640px] mx-auto"
          >
            Report cards are the one time all year a teacher has to account for
            everything they taught, in writing, to an audience outside the room.
            It should not be the week you dread.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              data-testid={user ? "rc-closing-cta-primary-open" : "rc-closing-cta-primary-try"}
              className="ae-cta h-12 px-6 rounded-lg text-sm font-bold inline-flex items-center gap-2"
            >
              <Link to={user ? "/dashboard?view=report-cards" : "/generator#skills-generator"}>
                {user ? "Open the Report Card Builder" : "Try for free"}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="rc-closing-cta-secondary-samples"
              className="h-12 px-6 rounded-lg text-sm font-bold border-[#4a6984]/25 text-[#1c2d3d] hover:bg-[#1c2d3d]/5 inline-flex items-center gap-2"
            >
              <Link to="/samples">See sample lessons</Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OntarioReportCards;
